import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import {
  AvatarSVG, DEFAULT_AVATAR,
  TOP_TYPE_M, TOP_TYPE_F, TOP_TYPE_HAT,
  ACCESSORIES, HAIR_COLOR, HAIR_COLOR_HEX,
  FACIAL_HAIR, CLOTHE_TYPE, CLOTHE_COLOR, CLOTHE_COLOR_HEX,
  EYE_TYPE, EYEBROW_TYPE, MOUTH_TYPE,
  SKIN_COLOR, SKIN_COLOR_HEX, BG_SWATCHES,
} from "@/lib/avatarBuilder";
import { Shuffle, X, Check, Sparkles } from "lucide-react";

// Human-readable labels for option ids — one map per language. PT is the
// canonical fallback when an option is missing from EN/ES.
const LABELS_PT = {
  // Tops
  NoHair: "Careca", Hat: "Chapéu", Hijab: "Hijab", Turban: "Turbante",
  WinterHat1: "Gorro 1", WinterHat2: "Gorro 2", WinterHat3: "Gorro 3", WinterHat4: "Gorro 4",
  Eyepatch: "Pala", LongHairBigHair: "Grande", LongHairBob: "Bob", LongHairBun: "Coque",
  LongHairCurly: "Encaracolado", LongHairCurvy: "Ondulado", LongHairDreads: "Rastas",
  LongHairFrida: "Frida", LongHairFro: "Afro", LongHairFroBand: "Afro c/banda",
  LongHairNotTooLong: "Médio", LongHairShavedSides: "Lateral rapado",
  LongHairMiaWallace: "Mia", LongHairStraight: "Liso", LongHairStraight2: "Liso 2",
  LongHairStraightStrand: "Madeixa",
  ShortHairDreads01: "Rastas curtas 1", ShortHairDreads02: "Rastas curtas 2",
  ShortHairFrizzle: "Frisado", ShortHairShaggyMullet: "Mullet",
  ShortHairShortCurly: "Curto encarac.", ShortHairShortFlat: "Curto liso",
  ShortHairShortRound: "Curto redondo", ShortHairShortWaved: "Curto ondul.",
  ShortHairSides: "Lados", ShortHairTheCaesar: "Cesar", ShortHairTheCaesarSidePart: "Cesar repartido",
  // Accessories
  Blank: "Nenhum", Round: "Redondos", Sunglasses: "Sol", Wayfarers: "Wayfarer",
  Prescription01: "Receita 1", Prescription02: "Receita 2", Kurt: "Kurt",
  // Facial hair
  BeardLight: "Barba leve", BeardMedium: "Barba média", BeardMajestic: "Barba majestosa",
  MoustacheFancy: "Bigode chique", MoustacheMagnum: "Bigode magnum",
  // Clothes
  ShirtCrewNeck: "T-shirt", ShirtScoopNeck: "Cava U", ShirtVNeck: "Cava V",
  Hoodie: "Hoodie", BlazerShirt: "Blazer", BlazerSweater: "Blazer suéter",
  CollarSweater: "Suéter gola", GraphicShirt: "Estampada", Overall: "Macacão",
  // Eyes
  Default: "Normal", Happy: "Felizes", Wink: "Piscando", WinkWacky: "Pisca maluco",
  Squint: "Apertados", Surprised: "Surpreso", Hearts: "Corações", Side: "De lado",
  Close: "Fechados", Cry: "Chorar", Dizzy: "Tonto", EyeRoll: "Revirar",
  // Eyebrows
  DefaultNatural: "Natural", RaisedExcited: "Levantadas", RaisedExcitedNatural: "Levant. natural",
  UpDown: "Cima/baixo", UpDownNatural: "Cima/baixo nat.", FlatNatural: "Planas",
  Angry: "Zangado", AngryNatural: "Zangado nat.", SadConcerned: "Triste",
  SadConcernedNatural: "Triste nat.", UnibrowNatural: "Unibrow",
  // Mouth
  Smile: "Sorriso", Twinkle: "Brilho", Tongue: "Língua de fora", Eating: "Comer",
  Serious: "Sério", Sad: "Triste", Concerned: "Preocupado", Disbelief: "Incrédulo",
  Grimace: "Careta", ScreamOpen: "A gritar", Vomit: "Aiii",
  // Skin
  Tanned: "Bronzeada", Yellow: "Amarela", Pale: "Pálida", Light: "Clara",
  Brown: "Castanha", DarkBrown: "Escura", Black: "Negra",
  // Hair colour
  BrownDark: "Castanho escuro", Auburn: "Auburn", Blonde: "Loiro",
  BlondeGolden: "Loiro dourado", Red: "Ruivo", PastelPink: "Rosa pastel",
  Platinum: "Platinado", SilverGray: "Prata",
};

const LABELS_EN = {
  NoHair: "Bald", Hat: "Hat", Hijab: "Hijab", Turban: "Turban",
  WinterHat1: "Beanie 1", WinterHat2: "Beanie 2", WinterHat3: "Beanie 3", WinterHat4: "Beanie 4",
  Eyepatch: "Eyepatch", LongHairBigHair: "Big Hair", LongHairBob: "Bob", LongHairBun: "Bun",
  LongHairCurly: "Curly", LongHairCurvy: "Wavy", LongHairDreads: "Dreads",
  LongHairFrida: "Frida", LongHairFro: "Afro", LongHairFroBand: "Afro w/band",
  LongHairNotTooLong: "Medium", LongHairShavedSides: "Shaved sides",
  LongHairMiaWallace: "Mia", LongHairStraight: "Straight", LongHairStraight2: "Straight 2",
  LongHairStraightStrand: "Strand",
  ShortHairDreads01: "Short dreads 1", ShortHairDreads02: "Short dreads 2",
  ShortHairFrizzle: "Frizzy", ShortHairShaggyMullet: "Mullet",
  ShortHairShortCurly: "Short curly", ShortHairShortFlat: "Short flat",
  ShortHairShortRound: "Short round", ShortHairShortWaved: "Short wavy",
  ShortHairSides: "Sides", ShortHairTheCaesar: "Caesar", ShortHairTheCaesarSidePart: "Caesar parted",
  Blank: "None", Round: "Round", Sunglasses: "Sunglasses", Wayfarers: "Wayfarer",
  Prescription01: "Rx 1", Prescription02: "Rx 2", Kurt: "Kurt",
  BeardLight: "Light beard", BeardMedium: "Medium beard", BeardMajestic: "Majestic beard",
  MoustacheFancy: "Fancy moustache", MoustacheMagnum: "Magnum moustache",
  ShirtCrewNeck: "T-shirt", ShirtScoopNeck: "Scoop neck", ShirtVNeck: "V-neck",
  Hoodie: "Hoodie", BlazerShirt: "Blazer", BlazerSweater: "Blazer sweater",
  CollarSweater: "Collar sweater", GraphicShirt: "Graphic", Overall: "Overall",
  Default: "Normal", Happy: "Happy", Wink: "Wink", WinkWacky: "Wacky wink",
  Squint: "Squint", Surprised: "Surprised", Hearts: "Hearts", Side: "Side",
  Close: "Closed", Cry: "Cry", Dizzy: "Dizzy", EyeRoll: "Eye roll",
  DefaultNatural: "Natural", RaisedExcited: "Raised", RaisedExcitedNatural: "Raised nat.",
  UpDown: "Up/down", UpDownNatural: "Up/down nat.", FlatNatural: "Flat",
  Angry: "Angry", AngryNatural: "Angry nat.", SadConcerned: "Sad",
  SadConcernedNatural: "Sad nat.", UnibrowNatural: "Unibrow",
  Smile: "Smile", Twinkle: "Twinkle", Tongue: "Tongue", Eating: "Eating",
  Serious: "Serious", Sad: "Sad", Concerned: "Concerned", Disbelief: "Disbelief",
  Grimace: "Grimace", ScreamOpen: "Scream", Vomit: "Yuck",
  Tanned: "Tanned", Yellow: "Yellow", Pale: "Pale", Light: "Light",
  Brown: "Brown", DarkBrown: "Dark brown", Black: "Black",
  BrownDark: "Dark brown", Auburn: "Auburn", Blonde: "Blonde",
  BlondeGolden: "Golden blonde", Red: "Red", PastelPink: "Pastel pink",
  Platinum: "Platinum", SilverGray: "Silver",
};

const LABELS_ES = {
  NoHair: "Calvo", Hat: "Sombrero", Hijab: "Hijab", Turban: "Turbante",
  WinterHat1: "Gorro 1", WinterHat2: "Gorro 2", WinterHat3: "Gorro 3", WinterHat4: "Gorro 4",
  Eyepatch: "Parche", LongHairBigHair: "Grande", LongHairBob: "Bob", LongHairBun: "Moño",
  LongHairCurly: "Rizado", LongHairCurvy: "Ondulado", LongHairDreads: "Rastas",
  LongHairFrida: "Frida", LongHairFro: "Afro", LongHairFroBand: "Afro c/cinta",
  LongHairNotTooLong: "Medio", LongHairShavedSides: "Lateral rapado",
  LongHairMiaWallace: "Mia", LongHairStraight: "Liso", LongHairStraight2: "Liso 2",
  LongHairStraightStrand: "Mechón",
  ShortHairDreads01: "Rastas cortas 1", ShortHairDreads02: "Rastas cortas 2",
  ShortHairFrizzle: "Crespo", ShortHairShaggyMullet: "Mullet",
  ShortHairShortCurly: "Corto rizado", ShortHairShortFlat: "Corto liso",
  ShortHairShortRound: "Corto redondo", ShortHairShortWaved: "Corto ondul.",
  ShortHairSides: "Lados", ShortHairTheCaesar: "César", ShortHairTheCaesarSidePart: "César raya",
  Blank: "Ninguno", Round: "Redondas", Sunglasses: "De sol", Wayfarers: "Wayfarer",
  Prescription01: "Receta 1", Prescription02: "Receta 2", Kurt: "Kurt",
  BeardLight: "Barba leve", BeardMedium: "Barba media", BeardMajestic: "Barba majestuosa",
  MoustacheFancy: "Bigote elegante", MoustacheMagnum: "Bigote magnum",
  ShirtCrewNeck: "Camiseta", ShirtScoopNeck: "Cuello U", ShirtVNeck: "Cuello V",
  Hoodie: "Sudadera", BlazerShirt: "Blazer", BlazerSweater: "Blazer suéter",
  CollarSweater: "Suéter cuello", GraphicShirt: "Estampada", Overall: "Peto",
  Default: "Normal", Happy: "Felices", Wink: "Guiño", WinkWacky: "Guiño loco",
  Squint: "Entrecerrados", Surprised: "Sorprendido", Hearts: "Corazones", Side: "De lado",
  Close: "Cerrados", Cry: "Llorar", Dizzy: "Mareado", EyeRoll: "Poner los ojos",
  DefaultNatural: "Natural", RaisedExcited: "Levantadas", RaisedExcitedNatural: "Levant. nat.",
  UpDown: "Arriba/abajo", UpDownNatural: "Arriba/abajo nat.", FlatNatural: "Planas",
  Angry: "Enfadado", AngryNatural: "Enfadado nat.", SadConcerned: "Triste",
  SadConcernedNatural: "Triste nat.", UnibrowNatural: "Uniceja",
  Smile: "Sonrisa", Twinkle: "Brillo", Tongue: "Lengua fuera", Eating: "Comer",
  Serious: "Serio", Sad: "Triste", Concerned: "Preocupado", Disbelief: "Incrédulo",
  Grimace: "Mueca", ScreamOpen: "Gritando", Vomit: "Puaj",
  Tanned: "Bronceada", Yellow: "Amarilla", Pale: "Pálida", Light: "Clara",
  Brown: "Morena", DarkBrown: "Oscura", Black: "Negra",
  BrownDark: "Castaño oscuro", Auburn: "Caoba", Blonde: "Rubio",
  BlondeGolden: "Rubio dorado", Red: "Pelirrojo", PastelPink: "Rosa pastel",
  Platinum: "Platino", SilverGray: "Plata",
};

const LABELS_BY_LANG = { pt: LABELS_PT, en: LABELS_EN, es: LABELS_ES };
const labelFor = (id, lang) => (LABELS_BY_LANG[lang]?.[id]) || LABELS_PT[id] || id;

// Tabs definition — each entry yields a grid of choices for one feature.
// Titles are i18n keys resolved at render time via `t()`.
const TABS_BASE = [
  { key: "eyes",      titleKey: "ava_eyes",        field: "eyeType",         options: EYE_TYPE,      kind: "thumb" },
  { key: "eyebrows",  titleKey: "ava_eyebrows",    field: "eyebrowType",     options: EYEBROW_TYPE,  kind: "thumb" },
  { key: "mouth",     titleKey: "ava_mouth",       field: "mouthType",       options: MOUTH_TYPE,    kind: "thumb" },
  { key: "accessories", titleKey: "ava_glasses",   field: "accessoriesType", options: ACCESSORIES,   kind: "thumb" },
  { key: "clothes",   titleKey: "ava_clothes",     field: "clotheType",      options: CLOTHE_TYPE,   kind: "thumb" },
  { key: "skin",      titleKey: "ava_skin",        field: "skinColor",       options: SKIN_COLOR,    kind: "color", hex: SKIN_COLOR_HEX },
  { key: "hairColor", titleKey: "ava_haircolor",   field: "hairColor",       options: HAIR_COLOR,    kind: "color", hex: HAIR_COLOR_HEX },
  { key: "clotheCol", titleKey: "ava_clothecolor", field: "clotheColor",     options: CLOTHE_COLOR,  kind: "color", hex: CLOTHE_COLOR_HEX },
];

const resolveGender = (cfg) => {
  if (!cfg) return "M";
  if (cfg.gender === "F" || cfg.gender === "M") return cfg.gender;
  // Heuristic: long hair top → female
  return (cfg.topType || "").startsWith("LongHair") ? "F" : "M";
};

export default function AvatarBuilder({ current, onSave, onClose }) {
  const { t, lang } = useI18n();
  const label = (id) => labelFor(id, lang);
  const initialGender = resolveGender(current);
  const [cfg, setCfg] = useState(() => ({
    ...DEFAULT_AVATAR,
    ...(current || {}),
    gender: initialGender,
  }));
  const [gender, setGender] = useState(initialGender);
  const [activeTab, setActiveTab] = useState("hair");
  const [bg, setBg] = useState(current?.bg || DEFAULT_AVATAR.bg);

  // Tabs are dynamic: hair/facial-hair lists depend on the chosen gender.
  const tabs = useMemo(() => {
    const tops = gender === "F"
      ? [...TOP_TYPE_F, ...TOP_TYPE_HAT, "NoHair"]
      : [...TOP_TYPE_M, ...TOP_TYPE_HAT];
    const list = [
      { key: "hair", titleKey: "ava_hair", field: "topType", options: tops, kind: "thumb" },
      ...TABS_BASE,
    ];
    if (gender === "M") {
      list.splice(3, 0,
        { key: "beard", titleKey: "ava_beard", field: "facialHairType", options: FACIAL_HAIR, kind: "thumb" },
        { key: "beardColor", titleKey: "ava_beardcolor", field: "facialHairColor", options: HAIR_COLOR, kind: "color", hex: HAIR_COLOR_HEX },
      );
    }
    return list;
  }, [gender]);

  const switchGender = (g) => {
    setGender(g);
    const defaultTop = g === "F" ? "LongHairStraight" : "ShortHairShortFlat";
    setCfg((prev) => ({
      ...prev,
      gender: g,
      topType: defaultTop,
      facialHairType: g === "F" ? "Blank" : prev.facialHairType,
    }));
  };

  const setField = (field, value) => setCfg((prev) => ({ ...prev, [field]: value }));

  const randomize = () => {
    const next = { ...cfg, gender };
    tabs.forEach((tab) => {
      next[tab.field] = tab.options[Math.floor(Math.random() * tab.options.length)];
    });
    setCfg(next);
    setBg(BG_SWATCHES[Math.floor(Math.random() * BG_SWATCHES.length)]);
  };

  const handleSave = () => {
    const final = { ...cfg, gender, bg };
    onSave(final);
    // Best-effort persist if the parent already wired the credentialId/trainingUserId
    // (kept inline so the modal works even when used in pure local-storage mode).
    if (current?.credentialId) {
      api.put(`/credentials/${current.credentialId}/avatar`, { avatar_cfg: final }).catch(() => {});
    }
  };

  const tab = tabs.find((tt) => tt.key === activeTab) || tabs[0];
  const previewCfg = { ...cfg, bg };

  return (
    <div className="fixed inset-0 z-[60] bg-black/65 backdrop-blur-sm grid place-items-center p-3 md:p-4 overflow-y-auto" data-testid="avatar-builder">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl p-4 md:p-6 shadow-2xl max-w-4xl w-full my-4 max-h-[94vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-[Fredoka] text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" /> {t("avatar_customize")}
          </h3>
          <div className="flex items-center gap-2">
            <button data-testid="btn-random-avatar" onClick={randomize} className="px-3 py-2 text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg font-bold inline-flex items-center gap-1">
              <Shuffle className="w-4 h-4" /> {t("random")}
            </button>
            <button data-testid="btn-close-avatar" onClick={onClose} className="text-slate-500 hover:text-slate-800 p-2 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Gender toggle */}
        <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-xl p-1.5 mb-5">
          <button
            data-testid="gender-M"
            onClick={() => switchGender("M")}
            className={`px-3 py-2 rounded-lg font-bold text-sm transition-all ${gender === "M" ? "bg-sky-500 text-white shadow" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >{t("male")}</button>
          <button
            data-testid="gender-F"
            onClick={() => switchGender("F")}
            className={`px-3 py-2 rounded-lg font-bold text-sm transition-all ${gender === "F" ? "bg-pink-500 text-white shadow" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >{t("female")}</button>
        </div>

        <div className="grid md:grid-cols-[230px_1fr] gap-4 md:gap-5">
          {/* LEFT: Preview + Save */}
          <div className="flex flex-col items-center gap-3">
            <motion.div
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-2xl border-2 border-slate-200 p-3 bg-gradient-to-br from-slate-50 to-white shadow-inner"
            >
              <AvatarSVG cfg={previewCfg} size={200} />
            </motion.div>

            <div className="w-full">
              <div className="text-xs font-bold text-slate-700 mb-1.5">{t("bg_color")}</div>
              <div className="grid grid-cols-6 gap-1.5">
                {BG_SWATCHES.map((swatch) => (
                  <button
                    key={swatch}
                    data-testid={`bg-${swatch}`}
                    onClick={() => setBg(swatch)}
                    className={`h-7 rounded-md border-2 transition-all ${bg === swatch ? "border-slate-900 scale-110" : "border-white shadow"}`}
                    style={{ background: swatch }}
                  />
                ))}
              </div>
            </div>

            <button
              data-testid="btn-save-avatar"
              onClick={handleSave}
              className="w-full px-4 py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold inline-flex items-center justify-center gap-2 shadow"
            >
              <Check className="w-4 h-4" /> {t("save_btn")}
            </button>
          </div>

          {/* RIGHT: Tabs + Options */}
          <div className="min-w-0">
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="avatar-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  data-testid={`tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-full transition-all ${activeTab === tab.key ? "bg-slate-900 text-white shadow" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                >
                  {t(tab.titleKey)}
                </button>
              ))}
            </div>

            {tab.kind === "thumb" && (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto pr-1" data-testid="options-grid">
                {tab.options.map((opt) => {
                  const isSel = cfg[tab.field] === opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`opt-${tab.key}-${opt}`}
                      onClick={() => setField(tab.field, opt)}
                      title={label(opt)}
                      className={`rounded-xl p-1.5 bg-white border-2 transition-all overflow-hidden ${isSel ? "border-sky-500 ring-2 ring-sky-200 scale-105" : "border-slate-200 hover:border-slate-400"}`}
                    >
                      <div className="aspect-square">
                        <AvatarSVG cfg={{ ...previewCfg, [tab.field]: opt }} size={80} />
                      </div>
                      <div className="text-[10px] font-bold text-center mt-1 truncate text-slate-700">
                        {label(opt)}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {tab.kind === "color" && (
              <div className="grid grid-cols-5 md:grid-cols-7 gap-2" data-testid="color-grid">
                {tab.options.map((opt) => {
                  const isSel = cfg[tab.field] === opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`opt-${tab.key}-${opt}`}
                      onClick={() => setField(tab.field, opt)}
                      title={label(opt)}
                      className={`rounded-xl p-2 bg-white border-2 transition-all ${isSel ? "border-sky-500 ring-2 ring-sky-200 scale-105" : "border-slate-200 hover:border-slate-400"}`}
                    >
                      <div className="w-full h-10 rounded-md" style={{ background: tab.hex[opt] || "#ccc" }} />
                      <div className="text-[10px] font-bold text-center mt-1 truncate text-slate-700">
                        {label(opt)}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
